import { Avatar, AvatarFallback, AvatarImage } from "@/styles/ui/avatar";
("use client");

import { LogOut } from "lucide-react";

import { AuthUser, useAuth } from "@/hooks/use-auth";
import { getEmailDisplayName } from "@/utils/user-display-name";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/styles/ui/dropdown-menu";
import { forwardRef } from "react";
import { signOutUser } from "@/services/auth-service";
import { ContactModal } from "@/components/common/contact-modal";
import { pushModal } from "@/stores/modal";

export const getDisplayName = (user: AuthUser) => {
  return (
    user.displayName || (user.email ? getEmailDisplayName(user.email) : "")
  );
};

export const UserAvatar = forwardRef<
  HTMLDivElement,
  { user: AuthUser } & React.ComponentProps<typeof Avatar>
>(({ user, ...props }, ref) => {
  const displayName = getDisplayName(user);
  const avatarFallback = displayName.split(" ")[0]?.charAt(0);
  return (
    <Avatar ref={ref} {...props} className="h-8 w-8 rounded-lg cursor-pointer">
      <AvatarImage src={user.photoURL || ""} alt={displayName} />
      <AvatarFallback className="rounded-lg bg-emerald-400">
        {avatarFallback}
      </AvatarFallback>
    </Avatar>
  );
});
UserAvatar.displayName = "UserAvatar";

export const UserInfoDropdown = ({
  side,
  children,
}: {
  side: "right" | "left" | "top" | "bottom";
  children: React.ReactNode;
}) => {
  const { user } = useAuth();

  if (!user) return null;
  const displayName = getDisplayName(user);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-56 rounded-lg bg-white !px-0 py-[4px]"
        side={side}
        align="end"
        sideOffset={15}
        alignOffset={0}
      >
        <DropdownMenuLabel className="!py-0 px-[4px] font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <UserAvatar user={user} />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{displayName}</span>
              <span className="truncate text-xs">{user.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuItem
          className="cursor-pointer hover:bg-[#F5F5F5] border-y border-[#E5E5E5] py-1.5 px-2 rounded-none"
          onClick={() => {
            pushModal(ContactModal);
          }}
        >
          문의하기
        </DropdownMenuItem>
        <DropdownMenuItem
          className="border-[#E5E5E5] py-1.5 px-2 rounded-none"
          disabled
        >
          마이페이지
        </DropdownMenuItem>
        <DropdownMenuItem
          className="border-t border-[#E5E5E5] py-1.5 px-2 rounded-none  cursor-pointer"
          onClick={() => signOutUser()}
        >
          <LogOut />
          로그아웃
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
